import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import type { Demand, ProjectSummary } from '@/lib/api';
import { DemandRichCard, type DemandStepInfo } from './DemandRichCard';
import { COLUMN_WF_TONE, type DemandColumnDef } from './demand-constants';

interface DemandMobileColumnProps {
  column: DemandColumnDef;
  demands: Demand[];
  onCardClick: (demand: Demand) => void;
  defaultOpen: boolean;
  /** Lista de projetos pra resolver o chip do card quando não há pipeline. */
  projects?: ProjectSummary[];
  /** Etapa atual por demanda (badge "Etapa X/Y" — só em execução). */
  stepByDemandId?: Record<string, DemandStepInfo | null>;
  /** Nome do negócio (business) por demanda — badge da linha 3 (DEM-2036). */
  businessNameById?: Record<string, string | null>;
}

// Reforma UX §6.4 — versão mobile do kanban de status: colunas empilhadas
// colapsáveis (extensão responsiva; o wireframe desktop rola na horizontal).
// Reusa DemandRichCard (mesmo card .drow do kanban desktop) e a anatomia do
// header de coluna (.wf-kdot tons + contador .wf-kc).
export function DemandMobileColumn({
  column,
  demands,
  onCardClick,
  defaultOpen,
  projects,
  stepByDemandId,
  businessNameById,
}: DemandMobileColumnProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-raios-border bg-raios-elevated/20">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2.5 border-b border-raios-border hover:bg-raios-elevated/30 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center gap-2">
          <span
            className={cn('wf-kdot', COLUMN_WF_TONE[column.id])}
            style={{ width: 8, height: 8 }}
            aria-hidden="true"
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-raios-muted">
            {column.kanbanTitle ?? column.title}
          </span>
          <span className="wf-kc">{demands.length}</span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-raios-muted transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="p-2 space-y-2">
          {demands.length === 0 ? (
            <p className="text-xs text-raios-muted text-center py-3">
              Sem demandas
            </p>
          ) : (
            demands.map((demand) => (
              <DemandRichCard
                key={demand.id}
                demand={demand}
                onClick={onCardClick}
                projects={projects}
                step={stepByDemandId?.[demand.id] ?? null}
                businessName={businessNameById?.[demand.id] ?? null}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
